import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ConsultationModal from "@/components/ConsultationModal";
import { Leaf, Sun, Heart, Droplets, Wind, ArrowRight, CheckCircle, Sparkles, Star } from "lucide-react";

const YOGA_IMG = "https://images.unsplash.com/photo-1545205597-3d9d02c29597?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const AYURVEDA_IMG = "https://images.unsplash.com/photo-1600334089648-b0d9d3028eb2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const MEDITATION_IMG = "https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const wellnessServices = [
  { icon: Leaf, title: "Ayurveda", desc: "Traditional Ayurvedic treatments including Panchakarma, herbal therapies, and personalized dosha-balancing programs.", color: "from-green-400 to-emerald-600" },
  { icon: Sun, title: "Yoga & Meditation", desc: "Guided yoga sessions, pranayama, and meditation programs for physical and mental wellness.", color: "from-amber-400 to-orange-600" },
  { icon: Heart, title: "Panchakarma Therapy", desc: "Five-step detoxification and rejuvenation program rooted in ancient Ayurvedic wisdom.", color: "from-rose-400 to-red-600" },
  { icon: Droplets, title: "Hydrotherapy", desc: "Water-based therapies for pain relief, detoxification, and rehabilitation.", color: "from-cyan-400 to-blue-600" },
  { icon: Wind, title: "Naturopathy", desc: "Natural healing methods including diet therapy, acupuncture, and lifestyle modification programs.", color: "from-teal-400 to-green-600" },
  { icon: Sparkles, title: "Preventive Health Checkups", desc: "Comprehensive health screening packages including blood tests, cardiac checkups, and full-body assessments.", color: "from-purple-400 to-indigo-600" },
];

const retreatPackages = [
  { name: "7-Day Wellness Retreat", price: "$1,200", includes: ["Accommodation at Govardhan Eco-village", "Daily Ayurvedic treatments", "Yoga & meditation sessions", "Organic vegetarian meals", "Airport transfers"] },
  { name: "14-Day Detox & Rejuvenation", price: "$2,200", includes: ["Full Panchakarma therapy", "Personal Ayurvedic doctor consultation", "Daily yoga & meditation", "Organic meals & herbal supplements", "Sightseeing tour included"] },
  { name: "21-Day Holistic Healing", price: "$3,000", includes: ["Complete holistic treatment plan", "Ayurveda + Naturopathy combined", "Spiritual wellness program", "All meals & accommodation", "Follow-up care plan"] },
];

// Floating particles component
function FloatingParticles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute w-2 h-2 bg-orange-400/20 rounded-full animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${5 + Math.random() * 5}s`,
          }}
        />
      ))}
    </div>
  );
}

// Animated counter component
function AnimatedCounter({ end, duration = 2000, suffix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

// Animated service card
function ServiceCard({ service, index }) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <Card 
      className="relative bg-white border border-slate-100 rounded-2xl p-6 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 group cursor-pointer"
      style={{ animationDelay: `${index * 100}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`wellness-service-${index}`}
    >
      {/* Gradient background on hover */}
      <div 
        className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
      />
      
      {/* Animated icon container */}
      <div className={`relative w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-4 transition-all duration-500 ${isHovered ? 'scale-110 rotate-3' : ''}`}>
        <service.icon className="h-7 w-7 text-white" />
        {/* Glow effect */}
        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${service.color} blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500`} />
      </div>
      
      <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
        {service.title}
      </h3>
      <p className="text-slate-500 text-sm leading-relaxed">{service.desc}</p>
      
      {/* Animated arrow */}
      <div className={`flex items-center gap-1 mt-4 text-orange-600 text-sm font-medium transition-all duration-300 ${isHovered ? 'translate-x-2' : ''}`}>
        Learn more <ArrowRight className="h-4 w-4" />
      </div>
    </Card>
  );
}

// Stats section with animations
function StatsSection() {
  const stats = [
    { value: 5000, suffix: "+", label: "Wellness Guests" },
    { value: 15, suffix: "+", label: "Years Experience" },
    { value: 50, suffix: "+", label: "Expert Practitioners" },
    { value: 30, suffix: "+", label: "Wellness Programs" },
  ];

  return (
    <section className="py-16 bg-gradient-to-r from-orange-600 to-orange-500 relative overflow-hidden">
      <FloatingParticles />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-8">
          <p className="text-orange-100 text-lg font-medium">Striving for 100% Patient Satisfaction</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <div key={i} className="text-center text-white">
              <div className="text-4xl md:text-5xl font-bold mb-2">
                <AnimatedCounter end={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-orange-100 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Animated retreat card
function RetreatCard({ pkg, index, isPopular }) {
  return (
    <Card 
      className={`relative bg-white rounded-2xl border p-8 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${
        isPopular ? "border-orange-500 ring-2 ring-orange-500/20" : "border-slate-100"
      }`}
      style={{ animationDelay: `${index * 150}ms` }}
      data-testid={`retreat-package-${index}`}
    >
      {/* Animated background pattern */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100 to-transparent rounded-full -translate-y-16 translate-x-16 opacity-50" />
      
      {isPopular && (
        <Badge className="bg-gradient-to-r from-orange-500 to-orange-600 text-white border-0 mb-4 animate-pulse">
          <Star className="h-3 w-3 mr-1" /> Most Popular
        </Badge>
      )}
      
      <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2 relative z-10">{pkg.name}</h3>
      
      <div className="relative z-10">
        <span className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">
          {pkg.price}
        </span>
        <span className="text-slate-400 text-sm ml-2">per person</span>
      </div>
      
      <ul className="space-y-3 my-8 relative z-10">
        {pkg.includes.map((item, j) => (
          <li key={j} className="flex items-start gap-3 text-sm text-slate-600 group">
            <div className="w-5 h-5 rounded-full bg-orange-100 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-orange-500 transition-colors">
              <CheckCircle className="h-3 w-3 text-orange-600 group-hover:text-white transition-colors" />
            </div>
            <span className="group-hover:text-slate-900 transition-colors">{item}</span>
          </li>
        ))}
      </ul>
      
      <ConsultationModal
        trigger={
          <Button 
            className={`w-full rounded-full font-semibold transition-all duration-300 ${
              isPopular 
                ? "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-orange-500/30" 
                : "bg-white border-2 border-orange-500 text-orange-600 hover:bg-orange-50"
            }`}
            data-testid={`retreat-book-${index}`}
          >
            Book This Package <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        }
      />
    </Card>
  );
}

// Main wellness page
export default function WellnessPage() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div data-testid="wellness-page" className="overflow-hidden">
      {/* Hero Section with Parallax */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-orange-50 via-white to-amber-50 overflow-hidden">
        {/* Animated background elements */}
        <div 
          className="absolute top-20 left-10 w-72 h-72 bg-orange-200/30 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        />
        <div 
          className="absolute bottom-20 right-10 w-96 h-96 bg-amber-200/30 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s', transform: `translateY(${-scrollY * 0.15}px)` }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <Badge className="bg-gradient-to-r from-orange-500 to-amber-500 text-white border-0 mb-6 font-body text-xs uppercase tracking-wider px-4 py-1">
                <Sparkles className="h-3 w-3 mr-1" /> Wellness & Holistic Tourism
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight" data-testid="wellness-title">
                Heal Your{" "}
                <span className="bg-gradient-to-r from-orange-600 to-amber-500 bg-clip-text text-transparent">
                  Body, Mind & Soul
                </span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                Experience the ancient healing wisdom of India at Vaidyaratnam Govardhan Eco-village. Our wellness programs combine Ayurveda, Yoga, Meditation, and Naturopathy for complete rejuvenation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <ConsultationModal
                  trigger={
                    <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-full px-8 py-6 font-semibold text-base shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all" data-testid="wellness-cta-btn">
                      Book Wellness Retreat <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  }
                />
                <Button variant="outline" className="border-2 border-orange-500 text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 font-semibold">
                  View Packages
                </Button>
              </div>
            </div>
            
            {/* Animated Image Gallery */}
            <div className="relative h-[500px] hidden lg:block">
              <div 
                className="absolute top-0 right-0 w-64 h-80 rounded-2xl overflow-hidden shadow-2xl animate-float z-20"
                style={{ animationDelay: '0s' }}
              >
                <img src={YOGA_IMG} alt="Yoga" className="w-full h-full object-cover" />
              </div>
              <div 
                className="absolute bottom-0 left-0 w-56 h-72 rounded-2xl overflow-hidden shadow-2xl animate-float z-10"
                style={{ animationDelay: '2s' }}
              >
                <img src={AYURVEDA_IMG} alt="Ayurveda" className="w-full h-full object-cover" />
              </div>
              <div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-64 rounded-2xl overflow-hidden shadow-2xl animate-float z-30"
                style={{ animationDelay: '1s' }}
              >
                <img src={MEDITATION_IMG} alt="Meditation" className="w-full h-full object-cover" />
              </div>
              
              {/* Decorative ring */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border-2 border-orange-200 rounded-full animate-spin-slow opacity-30" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsSection />

      {/* Services Section */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-orange-100 text-orange-600 border-0 mb-4">Our Services</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Holistic Wellness Services
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Comprehensive wellness programs designed to restore balance and vitality through ancient Indian healing traditions.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wellnessServices.map((service, i) => (
              <ServiceCard key={i} service={service} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Packages */}
      <section className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <Badge className="bg-gradient-to-r from-orange-500 to-amber-500 text-white border-0 mb-4">
              Govardhan Eco-village
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Retreat Packages
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              All-inclusive wellness retreats at the serene Govardhan Eco-village in Maharashtra, surrounded by nature and tranquility.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {retreatPackages.map((pkg, i) => (
              <RetreatCard key={i} pkg={pkg} index={i} isPopular={i === 1} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500 relative overflow-hidden">
        <FloatingParticles />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Start Your Healing Journey Today
          </h2>
          <p className="text-orange-100 text-lg mb-8 max-w-2xl mx-auto">
            Take the first step towards complete wellness. Our experts will create a personalized program just for you.
          </p>
          <ConsultationModal
            trigger={
              <Button className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-10 py-6 font-semibold text-lg shadow-2xl hover:shadow-white/20 transition-all">
                Get Free Consultation <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            }
          />
        </div>
      </section>
    </div>
  );
}
